import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { faCheck, faGift } from '@fortawesome/pro-solid-svg-icons';
import _ from 'lodash';
import moment from 'moment';
import cn from 'classnames';
import './OpportunityListingGifting.scss';

import { getLookbooks, canBrandSendGiftingOnlyOpportunities } from '../../../Helpers/user_helpers';
import { getOpportunityRequestsForOpportunity } from '../../../Helpers/brand_helpers';
import { isOpportunityRequestGiftingOnly, isOpportunityGiftingOnly } from '../../../Helpers/opportunity_helpers';

import Tooltip from '../../General/Tooltip';
import Select from '../../General/Select';

const OpportunityListingGifting = props => {
  const { analytics, user, opportunity, opportunityRequest, isEditing, AutoSend_Lookbook_id, updateField } = props;
  const { auto_send_lookbook } = opportunity || {};

  // Lookbooks
  const brandLookbooks = getLookbooks(user) || [];
  const existingRequests = getOpportunityRequestsForOpportunity(analytics, opportunity);
  const canSendGiftingOnlyOpps = canBrandSendGiftingOnlyOpportunities(user);
  const acceptedRequests = existingRequests.filter(request => request.userAccepted);
  const isEditingAutoSendLookbook = (auto_send_lookbook?.id || null) !== AutoSend_Lookbook_id;
  const lookbookOptions = [
    {
      value: null,
      label: 'None',
      sublabel: 'No lookbook will be sent automatically upon acceptance.'
    },
    ..._.orderBy(brandLookbooks, 'createdAt', 'desc').map(lookbook => ({
      value: lookbook.id,
      label: lookbook.title,
      sublabel: `Created on ${moment(lookbook.createdAt).format('MMM Do, YYYY')}`
    }))
  ];

  const showPanel = isEditing || canSendGiftingOnlyOpps || !!auto_send_lookbook;

  let isGiftingOnly = false;
  if (opportunityRequest) {
    // Creator Side
    isGiftingOnly = isOpportunityRequestGiftingOnly(opportunityRequest);
  }

  if (!showPanel) return null;

  return (
    <div className='opportunity-listing-section opportunity-listing-gifting-section'>
      <div className='opportunity-listing-section-title-container'>
        <div className='opportunity-listing-section-title'>
          Gifting
          {!!isGiftingOnly && <span className='disclaimer'>No Payment due to high value of lookbook</span>}
        </div>
      </div>
      {(!!isEditing || !!canSendGiftingOnlyOpps) && (
        <div className='gifting-actions'>
          <div className={cn('action-select', { 'is-active': !!AutoSend_Lookbook_id })}>
            <div className='action-label'>
              Auto-Send Lookbook
              <Tooltip
                message={`Use this if you want to automatically send a lookbook when a creator accepts the opportunity.${
                  acceptedRequests.length > 0
                    ? ` There ${acceptedRequests.length === 1 ? 'is' : 'are'} currently ${acceptedRequests.length} accepted request${
                        acceptedRequests.length === 1 ? '' : 's'
                      }, these will not receive the lookbook as this only applies to future requests.`
                    : ''
                }`}
              >
                {AutoSend_Lookbook_id ? <FontAwesomeIcon className='check' icon={faCheck} /> : <FontAwesomeIcon icon={faInfoCircle} />}
              </Tooltip>
            </div>
            <Select
              isDark
              options={lookbookOptions}
              value={AutoSend_Lookbook_id}
              placeholder='Attach a Lookbook'
              onChangeValue={newValue => updateField('AutoSend_Lookbook_id', newValue, { debounceDuration: 0 })}
              isSearching={false}
              isDisabled={false}
              isLoading={false}
            />
          </div>
        </div>
      )}
      {auto_send_lookbook && opportunity.AutoSend_Lookbook_id && (
        <div
          className={cn('opportunity-listing-auto-send-lookbook', {
            editing: isEditingAutoSendLookbook
          })}
        >
          <FontAwesomeIcon icon={faGift} />
          <div>
            Lookbook <Link to={`/lookbooks/${auto_send_lookbook.id}`}>{auto_send_lookbook.title}</Link> will be sent automatically upon acceptance.
          </div>
        </div>
      )}
    </div>
  );
};

OpportunityListingGifting.propTypes = {
  user: PropTypes.object.isRequired,
  analytics: PropTypes.object.isRequired,
  opportunity: PropTypes.object.isRequired,
  opportunityRequest: PropTypes.object,
  canEdit: PropTypes.bool.isRequired,
  isEditing: PropTypes.bool.isRequired,
// Removed unused props: description, guidelines, and additionalPerks.
  updateField: PropTypes.func,
  hasLoadedFullOpportunity: PropTypes.bool
};

export default OpportunityListingGifting;
